import * as z from "zod/v4";

export type Gid = `gid://shopify/${string}/${number}`;
export function getNumericIdFromGid(gid: Gid): `${number}` {
  return gid.split("/").pop()! as `${number}`; // still returned as a string - don't use for math
}

const gidRegex = /^gid:\/\/shopify\/(\w+)\/(\d+)$/;

function isGid(id: string): id is Gid {
  return gidRegex.test(id);
}

/**
 * Best effort that handles cases where we erroneously stored the gid instead of the id.
 */
export function parseNumericIdFromShopifyId(id: string): `${number}` {
  if (isGid(id)) {
    return getNumericIdFromGid(id);
  } else {
    return id as `${number}`;
  }
}

export enum ShopifyIdType {
  Article = "Article",
  Blog = "Blog",
  Collection = "Collection",
  Customer = "Customer",
  DeliveryCarrierService = "DeliveryCarrierService",
  DeliveryLocationGroup = "DeliveryLocationGroup",
  DeliveryProfile = "DeliveryProfile",
  DeliveryZone = "DeliveryZone",
  DraftOrder = "DraftOrder",
  DraftOrderLineItem = "DraftOrderLineItem",
  Duty = "Duty",
  EmailTemplate = "EmailTemplate",
  Fulfillment = "Fulfillment",
  FulfillmentEvent = "FulfillmentEvent",
  FulfillmentService = "FulfillmentService",
  InventoryItem = "InventoryItem",
  InventoryLevel = "InventoryLevel",
  LineItem = "LineItem",
  Location = "Location",
  MarketingEvent = "MarketingEvent",
  MediaImage = "MediaImage",
  Video = "Video",
  Model3D = "Model3D",
  ExternalVideo = "ExternalVideo",
  Metafield = "Metafield",
  Order = "Order",
  OrderTransaction = "OrderTransaction",
  Page = "Page",
  Product = "Product",
  ProductImage = "ProductImage",
  ProductVariant = "ProductVariant",
  Refund = "Refund",
  Shop = "Shop",
  StaffMember = "StaffMember",
  Theme = "Theme",
  GiftCard = "GiftCard",
  SellingPlan = "SellingPlan",
  CalculatedOrder = "CalculatedOrder",
  CalculatedLineItem = "CalculatedLineItem",
  MetafieldDefinition = "MetafieldDefinition",
  Publication = "Publication",
  MarketCatalog = "MarketCatalog",
  AppCatalog = "AppCatalog",
  CompanyLocationCatalog = "CompanyLocationCatalog",
}

export function convertShopifyIdToGid(
  gidOrNumericId: string,
  type: ShopifyIdType,
): Gid {
  if (isGid(gidOrNumericId)) {
    return gidOrNumericId;
  }
  return `gid://shopify/${type}/${gidOrNumericId}` as Gid;
}

function _shopifyGidSchema<T extends ShopifyIdType>(type: T) {
  return z
    .string()
    .regex(
      new RegExp(`^gid://shopify/${type}/\\d+$`),
      `Invalid Shopify ${type} GID format`,
    );
}

const ShopifyIdSchemas = {
  [ShopifyIdType.Customer]: _shopifyGidSchema(ShopifyIdType.Customer),
  [ShopifyIdType.Order]: _shopifyGidSchema(ShopifyIdType.Order),
  [ShopifyIdType.Product]: _shopifyGidSchema(ShopifyIdType.Product),
  [ShopifyIdType.ProductVariant]: _shopifyGidSchema(
    ShopifyIdType.ProductVariant,
  ),
  [ShopifyIdType.Collection]: _shopifyGidSchema(ShopifyIdType.Collection),
  [ShopifyIdType.DraftOrder]: _shopifyGidSchema(ShopifyIdType.DraftOrder),
  [ShopifyIdType.LineItem]: _shopifyGidSchema(ShopifyIdType.LineItem),
  [ShopifyIdType.GiftCard]: _shopifyGidSchema(ShopifyIdType.GiftCard),
  [ShopifyIdType.SellingPlan]: _shopifyGidSchema(ShopifyIdType.SellingPlan),
  [ShopifyIdType.CalculatedOrder]: _shopifyGidSchema(
    ShopifyIdType.CalculatedOrder,
  ),
  [ShopifyIdType.CalculatedLineItem]: _shopifyGidSchema(
    ShopifyIdType.CalculatedLineItem,
  ),
  [ShopifyIdType.DeliveryProfile]: _shopifyGidSchema(
    ShopifyIdType.DeliveryProfile,
  ),
  [ShopifyIdType.MetafieldDefinition]: _shopifyGidSchema(
    ShopifyIdType.MetafieldDefinition,
  ),
  [ShopifyIdType.Article]: _shopifyGidSchema(ShopifyIdType.Article),
  [ShopifyIdType.Blog]: _shopifyGidSchema(ShopifyIdType.Blog),
  [ShopifyIdType.DeliveryCarrierService]: _shopifyGidSchema(
    ShopifyIdType.DeliveryCarrierService,
  ),
  [ShopifyIdType.DeliveryLocationGroup]: _shopifyGidSchema(
    ShopifyIdType.DeliveryLocationGroup,
  ),
  [ShopifyIdType.DeliveryZone]: _shopifyGidSchema(ShopifyIdType.DeliveryZone),
  [ShopifyIdType.DraftOrderLineItem]: _shopifyGidSchema(
    ShopifyIdType.DraftOrderLineItem,
  ),
  [ShopifyIdType.Duty]: _shopifyGidSchema(ShopifyIdType.Duty),
  [ShopifyIdType.EmailTemplate]: _shopifyGidSchema(ShopifyIdType.EmailTemplate),
  [ShopifyIdType.Fulfillment]: _shopifyGidSchema(ShopifyIdType.Fulfillment),
  [ShopifyIdType.FulfillmentEvent]: _shopifyGidSchema(
    ShopifyIdType.FulfillmentEvent,
  ),
  [ShopifyIdType.FulfillmentService]: _shopifyGidSchema(
    ShopifyIdType.FulfillmentService,
  ),
  [ShopifyIdType.InventoryItem]: _shopifyGidSchema(ShopifyIdType.InventoryItem),
  [ShopifyIdType.InventoryLevel]: _shopifyGidSchema(
    ShopifyIdType.InventoryLevel,
  ),
  [ShopifyIdType.Location]: _shopifyGidSchema(ShopifyIdType.Location),
  [ShopifyIdType.MarketingEvent]: _shopifyGidSchema(
    ShopifyIdType.MarketingEvent,
  ),
  [ShopifyIdType.MediaImage]: _shopifyGidSchema(ShopifyIdType.MediaImage),
  [ShopifyIdType.Metafield]: _shopifyGidSchema(ShopifyIdType.Metafield),
  [ShopifyIdType.OrderTransaction]: _shopifyGidSchema(
    ShopifyIdType.OrderTransaction,
  ),
  [ShopifyIdType.Page]: _shopifyGidSchema(ShopifyIdType.Page),
  [ShopifyIdType.ProductImage]: _shopifyGidSchema(ShopifyIdType.ProductImage),
  [ShopifyIdType.Refund]: _shopifyGidSchema(ShopifyIdType.Refund),
  [ShopifyIdType.Shop]: _shopifyGidSchema(ShopifyIdType.Shop),
  [ShopifyIdType.StaffMember]: _shopifyGidSchema(ShopifyIdType.StaffMember),
  [ShopifyIdType.Theme]: _shopifyGidSchema(ShopifyIdType.Theme),
  [ShopifyIdType.Publication]: _shopifyGidSchema(ShopifyIdType.Publication),
  [ShopifyIdType.MarketCatalog]: _shopifyGidSchema(ShopifyIdType.MarketCatalog),
  [ShopifyIdType.AppCatalog]: _shopifyGidSchema(ShopifyIdType.AppCatalog),
  [ShopifyIdType.CompanyLocationCatalog]: _shopifyGidSchema(
    ShopifyIdType.CompanyLocationCatalog,
  ),
  [ShopifyIdType.Video]: _shopifyGidSchema(ShopifyIdType.Video),
  [ShopifyIdType.Model3D]: _shopifyGidSchema(ShopifyIdType.Model3D),
  [ShopifyIdType.ExternalVideo]: _shopifyGidSchema(ShopifyIdType.ExternalVideo),
} as const satisfies Record<ShopifyIdType, z.ZodString>;

export function shopifyGidSchema(type: ShopifyIdType) {
  return ShopifyIdSchemas[type];
}
